import { SpreadsheetCell } from '@functions/export-spreadsheet';
import { CustomFieldResponse, ECustomFieldType } from './services/responses/custom-field.response';

type Translate = (key: string) => string;

/// Valores viajam como texto no formato do tipo: número com ponto, data ISO, "true"/"false".
/// As regras são as do servidor, para o erro aparecer ao lado do campo antes de salvar.

export const TEXT_MAX_LENGTH = 500;

export const TYPE_KEY: Record<ECustomFieldType, string> = {
    [ECustomFieldType.Text]: 'camposPersonalizados.tipoTexto',
    [ECustomFieldType.Number]: 'camposPersonalizados.tipoNumero',
    [ECustomFieldType.Date]: 'camposPersonalizados.tipoData',
    [ECustomFieldType.List]: 'camposPersonalizados.tipoLista',
    [ECustomFieldType.Boolean]: 'camposPersonalizados.tipoSimNao',
};

/// Sem separador de milhar, como o servidor: "1.234" seria mil em pt-BR e um e pouco em inglês.
const NUMBER = /^[-+]?(\d+\.?\d*|\.\d+)$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/// 2026-02-30 passa na forma e não existe no calendário.
const isRealDate = (value: string) => {
    const match = ISO_DATE.exec(value);

    if (!match) return false;

    const [year, month, day] = match.slice(1).map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/// Chave i18n do erro, ou nulo se serve; o já gravado passa sem conferir (opção removida não trava).
export const customFieldValueError = (
    field: CustomFieldResponse,
    value: string | null | undefined,
    current?: string | null,
): string | null => {
    const text = (value ?? '').trim();

    if (!text) return field.required ? 'camposPersonalizados.erroObrigatorio' : null;
    if (text === current) return null;

    switch (field.type) {
        case ECustomFieldType.Text:
            return text.length <= TEXT_MAX_LENGTH ? null : 'camposPersonalizados.erroTextoLongo';
        case ECustomFieldType.Number:
            return NUMBER.test(text) ? null : 'camposPersonalizados.erroNumero';
        case ECustomFieldType.Date:
            return isRealDate(text) ? null : 'camposPersonalizados.erroData';
        case ECustomFieldType.List:
            return field.options.some((option) => option.toLowerCase() === text.toLowerCase())
                ? null
                : 'camposPersonalizados.erroOpcao';
        case ECustomFieldType.Boolean:
            return text === 'true' || text === 'false' ? null : 'camposPersonalizados.erroSimNao';
        default:
            return null;
    }
};

/// "2026-09-30" vira 30/09/2026 sem Date, que voltaria um dia no fuso do Brasil.
const formatIsoDate = (value: string) => {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

/// Como o valor aparece na tela e na planilha. Indefinido quando vazio.
export const displayCustomFieldValue = (
    field: CustomFieldResponse,
    value: string | null | undefined,
    t: Translate,
): string | undefined => {
    if (!value) return undefined;

    switch (field.type) {
        case ECustomFieldType.Boolean:
            return value === 'true' ? t('camposPersonalizados.sim') : t('camposPersonalizados.nao');
        case ECustomFieldType.Date:
            return formatIsoDate(value);
        case ECustomFieldType.Number:
            return Number(value).toLocaleString('pt-BR');
        default:
            return value;
    }
};

/// Número vai como número, para a planilha somar e ordenar.
export const customFieldExportCell = (
    field: CustomFieldResponse,
    value: string | null | undefined,
    t: Translate,
): SpreadsheetCell => {
    if (!value) return '';

    return field.type === ECustomFieldType.Number ? Number(value) : displayCustomFieldValue(field, value, t);
};

/// Colunas da planilha: ativos sempre, arquivados só se algum lead exportado ainda tem valor neles.
export const exportedCustomFields = (
    fields: CustomFieldResponse[],
    leads: { customFieldValues?: { customFieldId: string }[] }[],
): CustomFieldResponse[] =>
    fields.filter(
        (field) =>
            field.active ||
            leads.some((lead) => lead.customFieldValues?.some((value) => value.customFieldId === field.id)),
    );

/// Opções do seletor; o valor gravado entra mesmo sem estar na lista, senão salvar o apagaria.
export const customFieldOptions = (field: CustomFieldResponse, current?: string | null): string[] =>
    current && !field.options.includes(current) ? [...field.options, current] : field.options;
